import { type MonetaryValue } from 'ezmoney';
import { useSelector } from 'react-redux';

import { useWalletTransactionForecastQuery } from 'modules/company/hooks';
import { getShowWarningWhenNotEnoughFundsForWireTransfer } from 'modules/reimbursements/redux/selectors';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import {
  type ExpenseBill,
  type InvoiceBill,
} from 'src/core/modules/invoices/models';

import WireTransferModal from './WireTransferModal';

type Props = {
  amount: MonetaryValue;
  selectedPayments: Array<{
    id: string;
    bill: InvoiceBill | ExpenseBill | undefined;
    amount: MonetaryValue;
  }>;
  isSendingToPayment: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSendToPayment: (options?: { onSuccess: () => void }) => void;
  onComplete: () => void;
};

export const WireTransferModalContainer = ({
  amount,
  selectedPayments,
  isSendingToPayment,
  isOpen,
  onClose,
  onSendToPayment,
  onComplete,
}: Props) => {
  const showWarningWhenNotEnoughFundsForWireTransfer = useSelector(
    getShowWarningWhenNotEnoughFundsForWireTransfer,
  );

  const walletTransactionForecastQuery =
    useWalletTransactionForecastQuery()(amount);

  return (
    <QuerySuspense
      queryState={walletTransactionForecastQuery}
      loading={null}
      fallback={() => (
        // In case of error in the query, we fallback to the 'normal' flow (without not enough funds warning)
        // Since it shouldn't be blocking for the wire transfer flow
        <WireTransferModal
          selectedPayments={selectedPayments}
          isSendingToPayment={isSendingToPayment}
          isOpen={isOpen}
          showNotEnoughFundsWarning={false}
          onClose={onClose}
          onSendToPayment={onSendToPayment}
          onComplete={onComplete}
        />
      )}
    >
      {({ hasEnoughFunds }) => {
        const showNotEnoughFundsWarning =
          showWarningWhenNotEnoughFundsForWireTransfer && !hasEnoughFunds;

        return (
          <WireTransferModal
            selectedPayments={selectedPayments}
            isSendingToPayment={isSendingToPayment}
            isOpen={isOpen}
            showNotEnoughFundsWarning={showNotEnoughFundsWarning}
            onClose={onClose}
            onSendToPayment={onSendToPayment}
            onComplete={onComplete}
          />
        );
      }}
    </QuerySuspense>
  );
};
